/**
 * Users API service
 */

import { apiClient } from './client';
import type {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  ListUsersResponse,
} from '@/types/api';

/**
 * GET /api/users
 * List all users
 */
export async function getUsers(): Promise<ListUsersResponse> {
  const response = await apiClient.get<ListUsersResponse>('/users');
  return response.data;
}

/**
 * GET /api/users/:id
 * Get user by ID
 */
export async function getUserById(id: number): Promise<User> {
  const response = await apiClient.get<User>(`/users/${id}`);
  return response.data;
}

/**
 * GET /api/users/slug/:slug
 * Get user by slug
 */
export async function getUserBySlug(slug: string): Promise<User> {
  const response = await apiClient.get<User>(`/users/slug/${slug}`);
  return response.data;
}

/**
 * POST /api/users
 * Create a new user
 */
export async function createUser(data: CreateUserRequest): Promise<User> {
  const response = await apiClient.post<User>('/users', data);
  return response.data;
}

/**
 * PATCH /api/users/:id
 * Update user
 */
export async function updateUser(id: number, data: UpdateUserRequest): Promise<User> {
  const response = await apiClient.patch<User>(`/users/${id}`, data);
  return response.data;
}

/**
 * GET /api/users/me/manageable
 * Get list of users that current user can manage
 */
export async function getManageableUsers(): Promise<User[]> {
  const response = await apiClient.get<User[]>('/users/me/manageable');
  return response.data;
}

/**
 * GET /api/users/:id/delegates
 * Get list of delegates for a user (organizers only)
 */
export async function getDelegates(userId: number): Promise<User[]> {
  const response = await apiClient.get<User[]>(`/users/${userId}/delegates`);
  return response.data;
}

/**
 * POST /api/users/:id/delegates
 * Add a delegate for a user (organizers only)
 */
export async function addDelegate(userId: number, delegatedToUserId: number): Promise<void> {
  await apiClient.post(`/users/${userId}/delegates`, { delegatedToUserId });
}

/**
 * DELETE /api/users/:id/delegates/:delegateId
 * Remove a delegate from a user (organizers only)
 */
export async function removeDelegate(userId: number, delegateId: number): Promise<void> {
  await apiClient.delete(`/users/${userId}/delegates/${delegateId}`);
}
